/**
 * 공용 로그 출력 UI 경계.
 *
 * Owns:
 * - 로그 레벨별 강조와 연속 구간 렌더링.
 * - 사용자의 스크롤 위치를 존중하는 자동 하단 고정.
 *
 * Does not own:
 * - 로그 fetch·cursor polling·오류 재시도 정책.
 * - 소비자별 로그 상태와 안내 문구.
 */
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { groupLogLines } from "./_logLineLevel";
import styles from "./LogViewer.module.css";

// 하단 고정 판정 여유(px). 스크롤 값이 소수여도 "끝까지 내렸다" 로 인정하는 범위다.
const stickThresholdPx = 4;

// 커서 기반 로그 소비자가 공유하는 출력 컴포넌트다.
// 새 줄이 추가되면 맨 아래로 스크롤한다. 사용자가 위로 스크롤해 읽는 중이면 스크롤하지 않는다.
// 콘솔 팔레트와 조판은 고정이다. 상자 크기와 모서리만 컨테이너가 CSS 변수로 정한다.
export type LogViewerProps = { lines: readonly string[]; ariaLabel: string };

export function LogViewer({ lines, ariaLabel }: LogViewerProps): ReactNode {
  const viewportRef = useRef<HTMLPreElement | null>(null);
  const runs = useMemo(() => groupLogLines(lines), [lines]);
  // 하단 고정(stick-to-bottom) 여부. 사용자가 위로 스크롤하면 해제하고, 끝까지 내리면 다시 고정한다.
  const stickRef = useRef(true);

  function handleScroll(): void {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    stickRef.current =
      viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - stickThresholdPx;
  }

  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport && stickRef.current) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [lines]);

  return (
    <pre
      ref={viewportRef}
      onScroll={handleScroll}
      className={styles.logViewer}
      aria-label={ariaLabel}
    >
      {runs.map((run, index) => {
        // 구간 사이 개행은 뒤쪽 구간이 가진다. 색은 글자에만 적용되므로 개행 위치는 결과에 영향이 없다.
        const text = index === 0 ? run.text : `\n${run.text}`;
        if (run.highlight === null) {
          return text;
        }
        return (
          <span key={index} className={styles[run.highlight]}>
            {text}
          </span>
        );
      })}
    </pre>
  );
}
